// voiceInputState — what the chat bar's mic control says, and whether you can
// press it. One pure function, so the decision is testable without a browser,
// a microphone, or a running VoxyCore.
//
// Why this exists (Andres, 2026-08-07: "we have a visibility problem with the
// mic recording button"): the mic used to be a floating `VoxyWidget` pill
// centred over the 3D viewport. voxy styles its MUTED state as
// `opacity: .45; filter: grayscale(.6)` over its own hardcoded dark background —
// so a dark, faded pill sat on a dark scene, and since EVERY shipped blueprint
// sets `startMuted: true` (EventRuntime's voxyConfig node), that faded state is
// the one users boot into. The control was least visible in exactly the state
// you have to find it in. It now lives inside the chat input bar.
//
// The availability rule follows the shell's convention (DockedShell's rail,
// AppMenuBar): a thing you cannot do is LISTED and DISABLED with the reason
// inline, never hidden — hiding the mic when voice is broken is what made
// "the STT mic button is gone" un-diagnosable in the first place.

/** Shown when voxy was never started — no voxyConfig node in the live graph. */
export const NO_VOICE_REASON = 'Voice input is not enabled for this character'

/**
 * Derive the mic control's state.
 *
 * @param {object}  opts
 * @param {boolean} opts.active  voxyManager.isActive() — VoxyCore is capturing
 * @param {object?} opts.error   voxyManager.getLastError() — { code, message } | null
 * @param {boolean} opts.muted   voxy.getState() === 'MUTED'
 * @returns {{disabled: boolean, listening: boolean, title: string}}
 *   `listening` drives the input⇄waveform swap; `title` is the hover reason.
 */
export function resolveVoiceInput({ active = false, error = null, muted = true } = {}) {
  if (!active) {
    return { disabled: true, listening: false, title: unavailableReason(error), hint: null }
  }
  // Muted is the resting state: the field is a text box and the button starts
  // voice. Unmuted, voxy's VAD is live and the waveform owns the field.
  //
  // `hint` is the muted state saying so OUT LOUD. `title` is a hover tooltip,
  // and a tooltip is useless for the failure that actually happens: you boot
  // muted (every shipped blueprint sets startMuted), talk, and nothing occurs.
  // Nothing on screen connects the silence to the mic, so the mic looks broken
  // — which is exactly how this was reported. Only the resting state gets one:
  // an open mic already has the waveform saying it is live, and an unavailable
  // mic has its own reason in `title` (its hint would send the user clicking a
  // dead button).
  return muted
    ? {
      disabled: false, listening: false,
      title: 'Start voice input',
      hint: 'Mic muted — click to talk',
    }
    : {
      disabled: false, listening: true,
      title: 'Stop voice input and go back to typing',
      hint: null,
    }
}

function unavailableReason(error) {
  if (!error) return NO_VOICE_REASON
  if (error.code === 'mic-unavailable') {
    return 'Mic unavailable — check this site’s microphone permission, then reload'
  }
  const detail = error.message ? `: ${error.message}` : ''
  return `Voice input failed to start${detail}`
}
